import { formatDistanceToNow } from 'date-fns'
import { AlertCircle, Plus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { DocumentPreviewPane } from '@/components/AgentChat/DocumentPreviewPane'
import { useDocumentPreview } from '@/components/AgentChat/useDocumentPreview'
import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import { UserLabel } from '@/components/ui/user-label'
import {
  useAgentConversations,
  useCreateAgentConversation,
  useDeleteAgentConversation,
} from '@/hooks/useAgentConversation'
import { cn } from '@/lib/utils'
import { ChatPanel } from './ChatPanel'

export interface AgentChatScope {
  salesLead?: string
  organisation?: string
}

// Tailwind's @4xl (56rem): the width at which the chat list and the chat sit side by side.
const ROW_LAYOUT_MIN_WIDTH = 896

interface ScopedAgentChatProps {
  /** Scopes the conversation list + new conversations to a lead or a customer org. */
  scope: AgentChatScope
}

export function ScopedAgentChat({ scope }: ScopedAgentChatProps) {
  const scopeId = scope.salesLead ?? scope.organisation
  const [activeId, setActiveId] = useState<string | undefined>(undefined)

  const { conversations, isLoading } = useAgentConversations(scope)
  const createMutation = useCreateAgentConversation()
  const deleteMutation = useDeleteAgentConversation()
  const { previewDocuments, setFilledDocuments, pickDocument, closePreview } = useDocumentPreview(activeId)
  // Measured, not a viewport query: the page's action rail decides how wide this column is.
  const containerRef = useRef<HTMLDivElement>(null)
  const [canPreview, setCanPreview] = useState(false)
  const previewOpen = canPreview && previewDocuments.length > 0

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver(([entry]) =>
      setCanPreview(entry.contentRect.width >= ROW_LAYOUT_MIN_WIDTH)
    )
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  // Auto-select the most-recent conversation for this scope the first time we
  // see the list. Keyed on scopeId so navigating between leads/customers
  // re-selects for the new one.
  useEffect(() => {
    if (activeId || isLoading) return
    if (conversations.length > 0) {
      setActiveId(conversations[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, isLoading])

  const handleNew = () => {
    createMutation.mutate(scope, { onSuccess: (conv) => setActiveId(conv.id) })
  }

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    deleteMutation.mutate(id, {
      onSuccess: () => {
        if (id === activeId) setActiveId(undefined)
      },
    })
  }

  return (
    // The @container is its own element: a query can't measure the element it's declared on.
    <div ref={containerRef} className="@container h-[calc(100dvh-22rem)] min-h-[1000px] overflow-hidden">
      {/* Stacked until the column — which the page's action rail shrinks — can seat both. */}
      <div className="flex h-full flex-col gap-3 @4xl:flex-row">
        <div className="bg-card flex max-h-56 shrink-0 flex-col rounded-lg border @4xl:max-h-none @4xl:w-60">
          <div className="flex items-center justify-between gap-2 border-b p-3">
            <div className="text-sm font-medium">Chats</div>
            <Button
              size="icon-sm"
              variant="default"
              onClick={handleNew}
              disabled={createMutation.isPending}
              aria-label="New chat"
            >
              <Plus className="size-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <SkeletonRows className="p-3" rows={5} rowClassName="h-11" />
            ) : conversations.length === 0 ? (
              <div className="text-muted-foreground p-4 text-center text-xs">
                No chats yet. Start one with the + button.
              </div>
            ) : (
              <ul className="divide-y">
                {conversations.map((conv) => (
                  <li
                    key={conv.id}
                    onClick={() => setActiveId(conv.id)}
                    className={cn(
                      'group hover:bg-ink-50 flex cursor-pointer items-start gap-2 p-3',
                      conv.id === activeId && 'bg-brand-100 hover:bg-brand-100'
                    )}
                  >
                    <div className="flex-1 overflow-hidden">
                      <div className="flex items-center gap-1.5">
                        {conv.pendingConfirmation && (
                          <AlertCircle
                            className="size-3.5 shrink-0 text-amber-500"
                            aria-label="Pending confirmation"
                          />
                        )}
                        <div className="truncate text-sm font-medium">{conv.title}</div>
                      </div>
                      <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
                        <UserLabel
                          name={conv.createdByName}
                          src={conv.createdByAvatar}
                          className="gap-1.5"
                          avatarClassName="size-4"
                          fallbackClassName="text-[8px]"
                        />
                        <span className="shrink-0">
                          · {formatDistanceToNow(new Date(conv.dateUpdated), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                    {conv.isOwner && (
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        className="opacity-0 group-hover:opacity-100"
                        onClick={(e) => handleDelete(e, conv.id)}
                        aria-label="Delete conversation"
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* min-w-0: else the chat's content-based minimum widens this past the page column and spills under the action rail. */}
        <div className="min-h-0 min-w-0 flex-1">
          {activeId ? (
            <ChatPanel
              conversationId={activeId}
              onFilledDocuments={canPreview ? setFilledDocuments : undefined}
              onPreviewDocument={canPreview ? pickDocument : undefined}
            />
          ) : (
            <div className="bg-card text-muted-foreground flex h-full items-center justify-center rounded-lg border text-sm">
              {createMutation.isPending ? 'Starting new chat…' : 'Select a chat or start a new one.'}
            </div>
          )}
        </div>

        {previewOpen && (
          <DocumentPreviewPane
            docs={previewDocuments}
            onClose={closePreview}
            className="bg-card flex-1 rounded-lg border p-4"
          />
        )}
      </div>
    </div>
  )
}
